import {Alert, Button, Card, Col, Collapse, DatePicker, Divider, Flex, Form, Input, message, Row, Select} from "antd";
import {useForm} from "antd/es/form/Form";
import {createRef, useEffect, useRef, useState} from "react";
import axios from "axios";
import Password from "antd/es/input/Password";
import {
    CheckCircleFilled,
    CheckCircleOutlined,
    CheckSquareOutlined,
    ExclamationCircleOutlined,
    InfoCircleOutlined,
    InfoOutlined
} from "@ant-design/icons";
import React from 'react';
import {API} from "aws-amplify";
import TypographyCountry from "../CurrencySelect/TypographyCountry";
import {useLocation, useParams} from "react-router-dom";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import {connect} from "react-redux";
import {AppUtil} from "../../AppUtil";

dayjs.extend(utc);

// Live PHP receiving-bank codes, confirmed with the desk 2026-09-04. Adding a bank later is
// one line here. sTranBankCode goes straight into the BOC payload, so the option VALUE is the
// bare code - never the label, never the bank name.
export const BOC_BANK_CODES = [
    {name: 'Security Bank', code: '564'},
    {name: 'Union Bank of the Philippines', code: '587'}
];

const BOC_BANK_CODE_OPTIONS = BOC_BANK_CODES.map(bank => ({
    label: `${bank.name} (${bank.code})`,
    value: bank.code
}));

// mSendTransactionResult comes back from the BOC gateway as "{payoutId}|{status}|{detail}":
//   21616|1|OnRouteFRXSPL26090200000001   -> status 1, detail is the bank reference
//   21609|0|Unable to process your request -> status 0, detail is the error
// Getting a result back is NOT success. The status segment is the only thing that says so -
// reading "we got a response" as "it went" is the mistake that was made on 2026-09-03.
const BOC_RESULT_PATTERN = /^\s*(\d+)\|([01])\|([\s\S]*)$/;

// The gateway is a separate service (services.fwwportal.com) that we cannot change or read,
// so the field is looked for in the shapes it is known to take rather than one hardcoded
// path. If none of them yield a readable result the send is reported as UNKNOWN, never as
// success and never as failure.
// Payout ids are integers on both sides, so compare them as numbers. A string compare would
// call "021616" and "21616" a mismatch and send the operator off to do manual work over a
// formatting difference.
const samePayoutId = (a, b) => {
    if (a == null || b == null) { return false; }
    const left = Number(String(a).trim());
    const right = Number(String(b).trim());
    return Number.isFinite(left) && Number.isFinite(right) && left === right;
};

const readSendResult = (data) => {
    const pools = [data, data && data.result];
    for (const pool of pools) {
        if (pool == null || typeof pool !== 'object') { continue; }
        if (typeof pool.mSendTransactionResult === 'string') { return pool.mSendTransactionResult; }
    }
    for (const pool of pools) {
        if (pool == null || typeof pool !== 'object') { continue; }
        const shaped = Object.values(pool).find(value => typeof value === 'string' && BOC_RESULT_PATTERN.test(value));
        if (shaped != null) { return shaped; }
    }
    return null;
};

const BOCRemit = (props) => {

    // Re-entrancy guard for the send. A ref, not state: two Enter presses in the same tick
    // both read the pre-update componentState, so a state flag would let the second one
    // through and send the money twice.
    const sendInFlight = useRef(false);

    const [authenticationForm] = useForm();
    const [transferForm] = useForm();
    const inputTextArea = createRef();

    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const [componentState, setComponentState] = useState({
        disableAuthForm: true,
        authResponse: 'IDLE',
        countries: [],
        currencies: [],
        sessionId: null,
        isTransferLoading: false,
        payoutSaving: false,
        // Outcome of the last send attempt:
        //   null      nothing has been sent from this screen
        //   'SUCCESS' the bank accepted it - money has gone
        //   'FAILED'  the bank rejected it - no money has gone
        //   'UNKNOWN' we could not establish which - treated like a send, not like a failure,
        //             because a retry on an accepted payment pays the beneficiary twice
        sendOutcome: null,
        sendDetail: null,
        sendPayoutId: null,
        sendRef: null,
        // The transfer_payouts row we are allowed to write, or null if we could not agree on one
        payoutTargetId: null,
        payoutSaved: false,
        payoutSavedDate: null,
        payoutSaveError: null
    })

    const staffId = props.app_state && props.app_state.current_staff
        ? props.app_state.current_staff.id
        : (props.app_state ? props.app_state.current_staff_id : null);


    const fetchAuthenticationDetails = () => {
        setComponentState(prev => ({...prev, authResponse: 'CONNECTING' }));
        axios.post("https://services.fwwportal.com/boc-remit/authenticate", { type: 'AUTH' }).then(response => {
            const sessionId = response.data.result.requestSessionResult.split('|')[2];
            setComponentState(prev => ({...prev, authResponse: 'SUCCESS', sessionId: sessionId }));
        }).catch(err => {
            setComponentState(prev => ({...prev, authResponse: 'ERROR' }));
        })
    }
    const transferMoney = (values) => {
        // Enforced HERE, not just on the Transfer button. antd submits a Form when Enter is
        // pressed in a text input, so a disabled submit button does not stop a send - and the
        // network-error UNKNOWN path deliberately leaves the form enabled (it never learns
        // whether the bank took the money), which is exactly the outcome that must not be
        // repeated. A disabled button is a hint; this is the guard.
        const openedId = queryParams.get("paymentID");
        if (openedId == null || openedId === '') {
            message.error("No payout selected. Open this screen from the payments list before sending.");
            return;
        }
        if (componentState.sendOutcome === 'SUCCESS' || componentState.sendOutcome === 'UNKNOWN') {
            message.error("This payment has already been sent, or its outcome is unknown. Do not send it again - read the banner.");
            return;
        }
        if (sendInFlight.current) {
            return;
        }
        sendInFlight.current = true;

        // Clear the previous outcome. Only reachable from FAILED or from a screen that has not
        // sent yet; the guard above covers SUCCESS and UNKNOWN.
        setComponentState(prev => ({
            ...prev,
            isTransferLoading: true,
            sendOutcome: null,
            sendDetail: null,
            sendPayoutId: null,
            sendRef: null,
            payoutTargetId: null,
            payoutSaved: false,
            payoutSavedDate: null,
            payoutSaveError: null
        }));
        const loading = message.loading("Please wait while amount request is being processed. Please wait..", 0);
        console.log(values);

        const sender = {
            sRemFirstName: values.sRemFirstName,
            sRemLastName: values.sRemLastName,
            sRemAddrStreet: values.sRemAddrStreet,
            sRemAddrCity: values.sRemAddrCity,
            sRemAddrCountry: values.sRemAddrCountry,
            sRemAddrZip: values.sRemAddrZip,
            sRemContact: values.sRemContact,
            sRemBirthDate: values.sRemBirthDate,
            sRemCountryOfOrigin: values.sRemCountryOfOrigin
        };

        const receiver = {
            sBeneFirstName: values.sBeneFirstName,
            sBeneMiddleName: values.sBeneMiddleName,
            sBeneLastName: values.sBeneLastName,
            sBeneAddrStreet: values.sBeneAddrStreet,
            sBeneAddrCity: values.sBeneAddrCity,
            sBeneAddrCountry: values.sBeneAddrCountry,
            sBeneAddrZip: values.sBeneAddrZip,
            sBeneContact: values.sBeneContact,
            sBeneBirthDate: values.sBeneBirthDate,
        }

        const transaction = {
            sTranProductType: 'Credit to Account',
            sTranSource: 'FRXSPL',
            sTranValueDate: dayjs().format('MM/DD/YYYY'),
            sTranProcessDate: dayjs().format('MM/DD/YYYY'),
            sTranPaymentMode: 1,
            sTranAcctNo: values.sTranAcctNo,
            sTranAmtOrg: values.sTranAmtOrg,
            sTranFXRate: values.sTranFXRate,
            sTranAmtDes: values.sTranAmtDes,
            sTranCcyOrg: 'PHP',
            sTranCcyDes: 'PHP',
            sTranMsgToBene: values.sTranMsgToBene,
            sTranCoveringBank: '066',
            sTranSendingBank: '066',
            sTranBankCode: values.sTranBankCode,
            sTranOrderingBank: '066',
            sTranChannel: values.sTranChannel
        }

        const mainPayload = {
            sRemDet: sender,
            sBeneDet: receiver,
            sTranDet: transaction,
            paymentId: queryParams.get("paymentID")
        }

        const paymentId = queryParams.get("paymentID");

        axios.post(`https://services.fwwportal.com/boc-remit/send-transaction/${componentState.sessionId}`, mainPayload)
            .then(res => {
                console.log('res.data', res.data)

                const rawResult = readSendResult(res.data);
                const parsed = rawResult != null ? BOC_RESULT_PATTERN.exec(rawResult) : null;

                // The session is spent either way - the operator re-authenticates before another send.
                setComponentState(prev => ({ ...prev, sessionId: null, authResponse: 'IDLE' }));

                if (parsed == null) {
                    // The gateway answered with something we cannot read, so we do not know
                    // whether the bank took the money. Do not guess. transfer_payouts is left
                    // alone and re-sending from this screen is blocked.
                    transferForm.setFieldValue("json_response", JSON.stringify(res.data, null, 2));
                    finishSend('UNKNOWN', 'The gateway response could not be read. Check the Server Response box and confirm with Bank of Commerce before doing anything else.', null, rawResult, paymentId);
                    return;
                }

                const resultPayoutId = parsed[1];
                const status = parsed[2];
                const detail = parsed[3].trim();

                if (status === '1') {
                    message.success("Transfer sent");
                    transferForm.resetFields();
                } else {
                    message.error("Transfer rejected by Bank of Commerce");
                }
                transferForm.setFieldValue("json_response", JSON.stringify(res.data, null, 2));

                finishSend(status === '1' ? 'SUCCESS' : 'FAILED', detail, resultPayoutId, rawResult, paymentId);
        }).catch(err => {
            console.log(err);
            // A network-level failure cannot tell us whether the request reached the bank -
            // it may have been sent and the response lost. Reporting this as "failed" is
            // exactly what led to the duplicate payment, so it is UNKNOWN, not FAILED.
            transferForm.setFieldValue("json_response", JSON.stringify({ error: err && err.message ? err.message : String(err) }, null, 2));
            message.error("Transfer status could not be confirmed. Do not re-send - read the banner.");
            finishSend('UNKNOWN', `No usable response from the gateway (${err && err.message ? err.message : 'network error'}). The request may or may not have reached Bank of Commerce.`, null, null, paymentId);
        }).finally(() => {
            sendInFlight.current = false;
            loading();
        });

    }

    // Records the outcome of a send in the logging table. Written for every outcome, so that
    // "sent and rejected" is distinguishable from "never attempted" the next morning - the
    // absence of that distinction is what caused the 2026-09-03 duplicate payment.
    // Only ids and the raw gateway result go in: no names, no account numbers.
    // clientID is deliberately left unset - it is an actor field, and this is a staff action.
    const logSendOutcome = (logAction, detail, rawResult, paymentId, resultPayoutId) => {
        const transferId = queryParams.get("transferID");
        const beneficiaryId = queryParams.get("beneficiaryID");
        return API.post("commons", "/insert", {
            body: {
                context: 'logging',
                data: {
                    recordCreated: dayjs().utc().format('YYYY-MM-DD HH:mm:ss'),
                    logType: 'TRANSFER',
                    logAction: logAction,
                    staffID: staffId,
                    transferID: transferId != null ? Number(transferId) : null,
                    beneficiaryID: beneficiaryId != null ? Number(beneficiaryId) : null,
                    logDescription: `${logAction} for payout ${paymentId}: ${detail}`.substring(0, 255),
                    data: JSON.stringify({
                        payoutId: paymentId,
                        resultPayoutId: resultPayoutId,
                        transferId: transferId,
                        detail: detail,
                        mSendTransactionResult: rawResult
                    })
                }
            }
        }).catch(error => console.log('failed to write BOC send log', error));
    };

    // Writes the accepted send to transfer_payouts. payoutDate is what colours the payments
    // row green and disables the BOC button (PaymentsPage.jsx), so this write is what stops
    // the payment being sent a second time.
    // payoutBankId is deliberately omitted: entity_bank_accounts has no Bank of Commerce row
    // (31 = Global IME, 32 = MBL). update_transfer_payout truthy-guards each field, so the
    // other two land regardless. Adding the id is a one-line follow-up once the row exists.
    const savePayout = (payoutId, payoutRef) => {
        const payoutDate = dayjs().format('YYYY-MM-DD');
        setComponentState(prev => ({...prev, payoutSaving: true }));
        const loading = message.loading("Recording payout. Please wait...", 0);
        return API.put('transfers', `/update/update_transfer_payout/${payoutId}`, {
            body: {
                payoutDate: payoutDate,
                payoutRef: payoutRef
            }
        }).then(response => {
            console.log(response);
            // A 200 with no row matched is not a save. update_transfer_payout returns the raw
            // mysql result, so affectedRows tells us whether the payout id actually existed -
            // reporting "recorded" off the status code alone is how a save path lies.
            if (response && response.affectedRows === 0) {
                throw new Error(`No transfer_payouts row with id ${payoutId} - nothing was written.`);
            }
            // Record the row we actually wrote. On the id-mismatch override payoutTargetId is
            // null by design, so without this the success banner would name "payout null".
            setComponentState(prev => ({...prev, payoutSaving: false, payoutSaved: true, payoutSavedDate: payoutDate, payoutTargetId: payoutId, payoutSaveError: null }));
            message.success("Payout recorded");
        }).catch(error => {
            console.log(error);
            const reason = error && error.message ? error.message : 'Unknown error';
            setComponentState(prev => ({...prev, payoutSaving: false, payoutSaved: false, payoutSaveError: reason }));
            logSendOutcome('BOC_PAYOUT_SAVE_FAILED', reason, null, payoutId, null);
            message.error("Payout NOT recorded. The payment has still gone - read the banner.");
        }).finally(() => loading());
    };

    // Single exit point for every send, so no path can finish without recording something.
    const finishSend = (outcome, detail, resultPayoutId, rawResult, paymentId) => {
        // We only write the row the operator opened, and only when the bank echoed the same
        // id back. Writing payoutDate onto the wrong payout would both hide a real payment
        // and mark an unsent one as paid.
        const idsAgree = paymentId != null && (resultPayoutId == null || samePayoutId(resultPayoutId, paymentId));
        const targetId = outcome === 'SUCCESS' && idsAgree && resultPayoutId != null ? paymentId : null;

        setComponentState(prev => ({
            ...prev,
            isTransferLoading: false,
            sendOutcome: outcome,
            sendDetail: detail,
            sendPayoutId: resultPayoutId,
            sendRef: outcome === 'SUCCESS' ? detail : null,
            payoutTargetId: targetId,
            payoutSaved: false,
            payoutSaveError: outcome === 'SUCCESS' && targetId == null
                ? `The bank echoed payout ${resultPayoutId}, but this screen was opened for payout ${paymentId}. They must match before anything is written automatically.`
                : null
        }));

        logSendOutcome(`BOC_SEND_${outcome}`, detail, rawResult, paymentId, resultPayoutId);

        if (outcome === 'SUCCESS' && targetId != null) {
            savePayout(targetId, detail);
        }
    };


    const prefillPaymentDetail = async (queryParams) => {
        const loading = message.loading("Filling client details. Please wait..", 0);
        try {
            const clientId = queryParams.get('clientID');
            const [client] = await API.post("commons", "/fetch", {
                body: {
                    context: 'clients',
                    fields: ['*'],
                    condition: {id: clientId}
                }
            });

            const bId = queryParams.get('beneficiaryID');

            const [beneficiary] = await API.post("commons", "/fetch", {
                body: {
                    context: 'beneficiaries',
                    fields: ['*'],
                    condition: {id: bId}
                }
            });

            const transferId = queryParams.get('transferID');

            const [transfer] = await API.post("commons", "/fetch", {
                body: {
                    context: 'transfers',
                    fields: ['*'],
                    condition: {id: transferId}
                }
            });

            const paymentId = queryParams.get("paymentID");

            const payments = await API.get('payments', `/id/${paymentId}`, {});

            const {firstName, middleName, lastName} = AppUtil.getSplitNames(beneficiary.ben_legal_name);
            const beneficiaryPayload = {
                sBeneFirstName: firstName,
                sBeneLastName: lastName,
                sBeneMiddleName: middleName,
                sBeneAddrStreet: `${beneficiary.ben_address_line_1} ${beneficiary.ben_address_line_2}`,
                sBeneAddrCity: beneficiary.ben_address_suburb,
                sBeneAddrCountry: componentState.countries.find(item => beneficiary.ben_address_country === item.id).full_name,
                sBeneAddrZip: beneficiary.ben_address_postcode,
                sBeneContact: beneficiary.ben_telephone_mobile
            }

            const senderPayload = {
                sRemFirstName: client.first_name,
                sRemLastName: client.last_name,
                sRemAddrStreet: client.residential_street_line_1 + " " + client.residential_street_line_2,
                sRemAddrCity: client.residential_street_suburb,
                sRemAddrCountry: componentState.countries.find(item => item.id === client.residential_street_country).full_name,
                sRemAddrZip: client.residential_street_postcode,
                sRemContact: client.telephone_mobile,
                sRemBirthDate: dayjs(client.date_of_birth).format('MM/DD/YYYY'),
                sRemCountryOfOrigin: 'Australia',
            }

            // sTranBankCode is deliberately NOT prefilled - the operator picks the receiving bank
            // every time. beneficiaries.bank_code is unvalidated free text, is empty on every PH
            // beneficiary today, and a wrong bank code is what made payout 21609 fail. A field
            // this cheap to fill is not worth guessing at.
            const transactionPayload = {
                sTranAcctNo: beneficiary.account_number,
                sTranAmtOrg: payments.amount_to,
                sTranFXRate: 1,
                sTranAmtDes: payments.amount_to,
                sTranCcyOrg:  componentState.currencies.find(i => i.id === transfer.currency_from_id).iso_alpha_3,
                sTranCcyDes: componentState.currencies.find(i => i.id === transfer.currency_to_id).iso_alpha_3,
                sTranMsgToBene: payments.purposeOfPayment,
                sTranCoveringBank: '066',
                sTranOrderingBank: '066'
            }

            transferForm.setFieldsValue({
                ...beneficiaryPayload,
                ...senderPayload,
                ...transactionPayload,
                transfer_id: transferId,
                payment_id: payments.id
            });


        } catch (e) {
            console.log(e);
            message.error("Unable to prefill payments. Please fix client details");
        } finally {
            loading();
        }

    }

    const initialize = async () => {
        const [countries, currencies] = await Promise.all(
            [API.post("commons", "/fetch", {
                body: {
                    context: 'countries',
                    fields: ['*']
                }
            }),
            API.post("commons", "/fetch", {
                body: {
                    context: 'currencies',
                    fields: ['*']
                }
            })]
        );
        setComponentState(prev => ({...prev, countries: countries, currencies: currencies}));
    }

    useEffect(() => {
       if (componentState.countries.length > 0) {
           // fetchAuthenticationDetails();
           if (queryParams.get('paymentID') != null) {
               prefillPaymentDetail(queryParams);
           }
       }
    }, [componentState.countries]);

    // Persistent on-screen record of the last send. Deliberately an Alert and not a toast:
    // the operator has to still be able to see, minutes or hours later, whether this payment
    // went.
    //
    // Rendered TWICE - once above the form and once directly under the Transfer button. The
    // Transfer button is at the bottom of a long form, so that is where the operator is
    // looking when the result lands; assuming they will scroll up to find out what happened
    // is how a failed send gets missed. Both copies come from this one function so their
    // wording cannot drift apart.
    //
    // Both are placed OUTSIDE the transfer Form so that antd's disabled context (which the
    // Form applies to everything inside it, Buttons included) cannot reach the retry button -
    // that trap is what made the old "Save Payout" button unreachable after a send.
    const renderSendBanner = () => {
        const openedPayoutId = queryParams.get("paymentID");

        if (componentState.sendOutcome == null) {
            if (openedPayoutId == null || openedPayoutId === '') {
                return <Alert style={{marginTop: 16, marginBottom: 16}} showIcon={true} type={'error'}
                              message={'No payout selected - sending is disabled'}
                              description={<>
                                  <div>This screen was opened without a paymentID, so there is no transfer_payouts row to record a send against.</div>
                                  <div>Open it from the payments list (Back Office &gt; Payments &gt; BOC) so the payout is known before any money moves.</div>
                              </>}/>;
            }
            return null;
        }

        if (componentState.sendOutcome === 'SUCCESS' && componentState.payoutSaved) {
            return <Alert style={{marginTop: 16, marginBottom: 16}} showIcon={true} type={'success'}
                          message={`Transfer sent and recorded - payout ${componentState.payoutTargetId}`}
                          description={<>
                              <div>Bank of Commerce accepted this payment. Bank reference: <strong>{componentState.sendRef}</strong></div>
                              <div>Recorded against transfer_payouts #{componentState.payoutTargetId}, payout date {dayjs(componentState.payoutSavedDate).format('DD-MM-YYYY')}.</div>
                              <div>The payments list now shows this payout as paid and its BOC button is disabled. Do not send it again.</div>
                          </>}/>;
        }

        if (componentState.sendOutcome === 'SUCCESS') {
            return <Alert style={{marginTop: 16, marginBottom: 16}} showIcon={true} type={'error'}
                          message={'THE PAYMENT HAS GONE - but it was NOT recorded'}
                          description={<>
                              <div>Bank of Commerce accepted this payment. Bank reference: <strong>{componentState.sendRef}</strong></div>
                              <div>Writing it to transfer_payouts failed: {componentState.payoutSaveError}</div>
                              <div>Until it is recorded the payments list still shows this payout as unpaid and its BOC button stays enabled, so it can be paid a second time.</div>
                              {componentState.payoutTargetId != null
                                  ? <div style={{marginTop: 8}}>
                                      {/* disabled={false} is explicit on purpose - see the note above renderSendBanner */}
                                      <Button type={'primary'} danger disabled={false}
                                              loading={componentState.payoutSaving}
                                              onClick={() => savePayout(componentState.payoutTargetId, componentState.sendRef)}>Retry recording payout</Button>
                                  </div>
                                  : <div style={{marginTop: 8}}>
                                      <div>Check with Bank of Commerce which payout this reference belongs to before recording it.</div>
                                      {openedPayoutId != null && openedPayoutId !== ''
                                          ? <div style={{marginTop: 8}}>
                                              {/* disabled={false} is explicit on purpose - see the note above renderSendBanner */}
                                              <Button type={'primary'} danger disabled={false}
                                                      loading={componentState.payoutSaving}
                                                      onClick={() => savePayout(openedPayoutId, componentState.sendRef)}>Record against payout {openedPayoutId} anyway</Button>
                                          </div>
                                          : null}
                                  </div>}
                          </>}/>;
        }

        if (componentState.sendOutcome === 'FAILED') {
            return <Alert style={{marginTop: 16, marginBottom: 16}} showIcon={true} type={'error'}
                          message={'Transfer REJECTED by Bank of Commerce - no money has gone'}
                          description={<>
                              <div>The bank returned: <strong>{componentState.sendDetail}</strong></div>
                              <div>Nothing was written to transfer_payouts. Payout {openedPayoutId} is still unpaid and its BOC button on the payments list is still enabled.</div>
                              <div>Fix the problem (a blank or wrong Bank Code is the usual cause), check the connection again and re-send.</div>
                          </>}/>;
        }

        return <Alert style={{marginTop: 16, marginBottom: 16}} showIcon={true} type={'warning'}
                      message={'Transfer status UNKNOWN - do NOT send this payment again'}
                      description={<>
                          <div>{componentState.sendDetail}</div>
                          <div>Nothing was written to transfer_payouts, because we cannot tell whether the bank took the money.</div>
                          <div>Confirm with Bank of Commerce whether payout {openedPayoutId} was received. If it was, record it by hand. If it was not, reload this page and send again.</div>
                      </>}/>;
    };

    useEffect(() => {
        initialize();
    }, [0]);

    // A send that succeeded, or whose outcome we could not establish, must not be repeatable
    // from this screen. undefined (not false) when unblocked, so that the Form's own disabled
    // state still governs the button - antd Button takes an explicit disabled prop over the
    // Form's disabled context, so disabled={false} here would let it be clicked before the
    // connection check has passed.
    const sendBlocked = componentState.sendOutcome === 'SUCCESS' || componentState.sendOutcome === 'UNKNOWN';

    // No payout id means there is nothing to record the send against - the money could go and
    // leave no row to write to. Do not allow a send at all in that state.
    const openedPayoutId = queryParams.get("paymentID");
    const missingPayoutId = openedPayoutId == null || openedPayoutId === '';

    return (
        <>
            <Card title={'Bank of Commerce (Philippines) Money Transfer'}>
                <Form name={'authentication-form'}
                      labelCol={{ span: 3 }}
                      labelAlign={'left'}
                      form={authenticationForm}
                      onFinish={fetchAuthenticationDetails}>
                    <Flex justify={'start'}>
                        <Form.Item>
                            <Button disabled={componentState.authResponse === 'SUCCESS'}
                                    loading={componentState.authResponse === 'CONNECTING'}
                                    htmlType={'submit'} type={'primary'} icon={componentState.sessionId ? <CheckCircleOutlined /> : null}>Check Connection</Button>
                        </Form.Item>
                        <Form.Item>
                            <Button type={'link'} onClick={() => fetchAuthenticationDetails()}>Retry</Button>
                        </Form.Item>
                    </Flex>
                </Form>
                {
                    componentState.authResponse === 'SUCCESS'
                    ? <Alert message={'Authentication Successful. Please Proceed with Transaction'} showIcon={true} icon={<CheckCircleFilled />} type={'success'}/>
                        : (componentState.authResponse === 'ERROR' ? <Alert message={'Connection failure. Please try again or contact support.'} showIcon={true} icon={<ExclamationCircleOutlined />} type={'error'}/>
                            : <Alert message={'Please check connection before proceeding with transactions.'} showIcon={true} icon={<InfoCircleOutlined />} type={'info'}/>)
                }

                <Divider />

                {renderSendBanner()}

                <Form initialValues={{ sTranChannel: "CTO" }} labelCol={{span: 5}} labelAlign={'left'} name={'transfer-form'} disabled={componentState.authResponse === 'ERROR' || componentState.authResponse === 'IDLE' } form={transferForm}
                      onFinish={transferMoney}>
                    <Collapse
                        defaultActiveKey={1}
                        items={[{
                            key: '1',
                            label: 'Sender Details / Beneficiary Details',
                            children: <>
                                <Row gutter={[16, 16]}>
                                    <Col xs={24} sm={24} md={24} lg={12} xl={12} xxl={12}>
                                        <h3>Sender Details</h3>
                                        <Divider />
                                        <Form.Item name={'sRemFirstName'} label={'First Name'} rules={[{required: true, message: 'Field is required'}]}>
                                            <Input/>
                                        </Form.Item>
                                        <Form.Item name={'sRemLastName'} label={'Last Name'} rules={[{required: true, message: 'Field is required'}]}>
                                            <Input/>
                                        </Form.Item>
                                        <Form.Item name={'sRemAddrStreet'} label={'Street Address'} rules={[{required: true, message: 'Field is required'}]}>
                                            <Input/>
                                        </Form.Item>
                                        <Form.Item name={'sRemAddrCity'} label={'City'} rules={[{required: true, message: 'Field is required'}]}>
                                            <Input/>
                                        </Form.Item>
                                        <Form.Item name={'sRemAddrCountry'} label={'Country'} rules={[{required: true, message: 'Field is required'}]}>
                                            <Select allowClear={true}
                                                    showSearch={true}
                                                    optionFilterProp={'alt'}
                                                    options={componentState.countries.map(i => ({
                                                        label: <TypographyCountry
                                                            iso_alpha_2={i.iso_alpha_2}>{i.full_name}</TypographyCountry>,
                                                        alt: i.full_name,
                                                        value: i.full_name
                                                    }))}></Select>
                                        </Form.Item>
                                        <Form.Item name={'sRemAddrZip'} label={'ZIP Code'} rules={[{required: true, message: 'Field is required'}]}>
                                            <Input/>
                                        </Form.Item>
                                        <Form.Item name={'sRemContact'} label={'Telephone'} rules={[{required: true, message: 'Field is required'}]}>
                                            <Input/>
                                        </Form.Item>
                                        <Form.Item name={'sRemBirthDate'} label={'Birth Date'}
                                                   normalize={(e) => e && dayjs(e).startOf('day').format('MM/DD/YYYY')}
                                                   getValueProps={(value) =>
                                                       value && {value: dayjs(value, 'DD/MM/YYYY').startOf("day")}
                                                   }>
                                            <DatePicker format={'DD/MM/YYYY'} />
                                        </Form.Item>
                                        <Form.Item name={'sRemCountryOfOrigin'} label={'Country of Origin'} rules={[{required: true, message: 'Field is required'}]}>
                                            <Select allowClear={true}
                                                    showSearch={true}
                                                    optionFilterProp={'alt'}
                                                    options={componentState.countries.map(i => ({
                                                        label: <TypographyCountry
                                                            iso_alpha_2={i.iso_alpha_2}>{i.full_name}</TypographyCountry>,
                                                        alt: i.full_name,
                                                        value: i.full_name
                                                    }))}></Select>
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={24} md={24} lg={12} xl={12} xxl={12}>
                                        <h3>Beneficiary Details</h3>
                                        <Divider/>
                                        <Form.Item name={'sBeneFirstName'} label={'First Name'} required={[{required: true, message: 'Field is required'}]}>
                                            <Input/>
                                        </Form.Item>
                                        <Form.Item name={'sBeneMiddleName'} label={'Middle Name'} required={[{required: true, message: 'Field is required'}]}>
                                            <Input/>
                                        </Form.Item>
                                        <Form.Item name={'sBeneLastName'} label={'Last Name'} required={[{required: true, message: 'Field is required'}]}>
                                            <Input/>
                                        </Form.Item>
                                        <Form.Item name={'sBeneAddrStreet'} label={'Street Address'} required={[{required: true, message: 'Field is required'}]}>
                                            <Input/>
                                        </Form.Item>
                                        <Form.Item name={'sBeneAddrCity'} label={'City'} required={[{required: true, message: 'Field is required'}]}>
                                            <Input/>
                                        </Form.Item>
                                        <Form.Item name={'sBeneAddrCountry'} label={'Country'} required={[{required: true, message: 'Field is required'}]}>
                                            <Select allowClear={true}
                                                    showSearch={true}
                                                    optionFilterProp={'alt'}
                                                    options={componentState.countries.map(i => ({
                                                        label: <TypographyCountry
                                                            iso_alpha_2={i.iso_alpha_2}>{i.full_name}</TypographyCountry>,
                                                        alt: i.full_name,
                                                        value: i.full_name
                                                    }))}></Select>
                                        </Form.Item>
                                        <Form.Item name={'sBeneAddrZip'} label={'ZIP Code'} required={[{required: true, message: 'Field is required'}]}>
                                            <Input/>
                                        </Form.Item>
                                        <Form.Item name={'sBeneContact'} label={'Contact'} required={[{required: true, message: 'Field is required'}]}>
                                            <Input/>
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </>
                        }]}
                    />
                    <Divider />
                    <Collapse
                        defaultActiveKey={3}
                        items={[{
                            key: '3',
                            label: 'Banking Details',
                            children: <>
                                <Row gutter={[16, 16]}>
                                    <Col xs={24} sm={24} md={24} lg={12} xl={12} xxl={12}>
                                        <Form.Item name={'sTranAcctNo'} label={'Account Number'}>
                                            <Input />
                                        </Form.Item>
                                        <Form.Item name={'sTranAmtOrg'} label={'Amount From'}>
                                            <Input />
                                        </Form.Item>
                                        <Form.Item name={'sTranFXRate'} label={'Exchange Rate'}>
                                            <Input />
                                        </Form.Item>
                                        <Form.Item name={'sTranAmtDes'} label={'Destination Amount'}>
                                            <Input />
                                        </Form.Item>
                                        <Form.Item name={'sTranMsgToBene'} label={'Purpose of Payment'}>
                                            <Input />
                                        </Form.Item>
                                        <Form.Item name={'sTranChannel'} label={'Transaction Channel'}
                                                   help={"CTA (bank of commerce) | IPAY (<50k php) | PNET (>50k php)"}
                                        >
                                            <Select defaultValue={'CTA'} options={[
                                                {label: 'IPAY', value: 'IPAY'},
                                                {label: 'CTA', value: 'CTA'},
                                                {label: 'PNET', value: 'PNET'},
                                            ]}></Select>
                                        </Form.Item>
                                        <Form.Item name={'sTranBankCode'} label={'Bank Code'} rules={[{required: true, message: 'Field is required'}]}>
                                            <Select allowClear={true}
                                                    showSearch={true}
                                                    optionFilterProp={'label'}
                                                    options={BOC_BANK_CODE_OPTIONS}></Select>
                                        </Form.Item>
                                        <Form.Item name={'sTranCcyDes'} hidden>
                                            <Input />
                                        </Form.Item>
                                        <Form.Item name={'sTranCcyOrg'} hidden>
                                            <Input />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={24} md={24} lg={12} xl={12} xxl={12}>
                                        <Form.Item name={'json_response'} label={'Server Response'}>
                                            <Input.TextArea ref={inputTextArea} size={'large'}></Input.TextArea>
                                        </Form.Item>
                                        <Form.Item name={'transfer_id'} label={'Transfer ID'}>
                                            <Input />
                                        </Form.Item>
                                        <Form.Item name={'payment_id'} label={'Payment ID'}>
                                            <Input />
                                        </Form.Item>
                                        <Form.Item name={'sTranCoveringBank'} label={'Source Bank'}>
                                            <Input />
                                        </Form.Item>
                                        <Form.Item name={'sTranOrderingBank'} hidden>
                                            <Input />
                                        </Form.Item>
                                        <Form.Item name={'bank_confirmation'} label={'Bank Confirmation'}>
                                            <Input />
                                        </Form.Item>
                                        {/* Retired 2026-09-04. savePayoutResponse() only console.logged the form and wrote
                                            nothing (Known Issues #36), and sitting inside this Form it was disabled by antd's
                                            disabled context the moment a send succeeded - unreachable exactly when it mattered.
                                            The payout is now written automatically by finishSend(); the retry lives in the
                                            banner above the form, outside the disabled context.
                                        <Button type={'primary'} loading={componentState.payoutSaving} onClick={() => savePayoutResponse()}>Save Payout</Button>
                                        */}
                                    </Col>
                                </Row>
                            </>
                        }]}
                    />
                    <Form.Item style={{ marginTop: 10 }}>
                        <Button type={'primary'} size={'large'} htmlType={'submit'}
                                loading={componentState.isTransferLoading}
                                disabled={(sendBlocked || missingPayoutId) ? true : undefined}>Transfer</Button>
                    </Form.Item>
                </Form>

                {/* Same banner again, where the operator actually is after clicking Transfer. */}
                {renderSendBanner()}
            </Card>
        </>
    )
}

const mapStateToProps = state => {
    return {
        app_state: state.app_state
    };
};

export default connect(mapStateToProps)(BOCRemit);